// motion.js — tokens de movimiento y overrides de transición de MUI (#1523).
//
// Regla de Sebastián (02-oct-2026): el Modo Turno se tiene que seguir viendo
// EXACTAMENTE igual; solo cambia cómo entran, salen o cambian las cosas. Por
// eso acá no hay un solo color, tamaño ni posición: todo es duración, easing y
// animaciones que al terminar dejan el elemento como estaba. La animación del
// diálogo usa fill-mode `backwards` (no `both`) justamente para que, al
// terminar, el Paper vuelva a sus estilos de siempre en vez de quedar con un
// `transform` fijado que en reposo cambiaría el render.
//
// Vive aparte de theme.js y se FUSIONA con el theme ya creado
// (`withMotion`) en vez de declarar `MuiDialog`/`MuiMenu` dentro de
// `components`: si otra sesión agrega un override de color a esas mismas
// claves, un objeto literal con la clave repetida haría que el último gane
// en silencio. Fusionando, los dos conviven.
//
// Perfil elegido: opción B "Física tipo Apple" de
// tools/mockups/smartdx_mockups_transiciones_turno.html.
import { keyframes } from "@emotion/react";

// Duraciones en ms. La salida es siempre más corta que la entrada.
export const MOTION = {
  fast: 160, // micro: íconos, chips, estados de botón
  base: 220, // menús, popovers, snackbars
  panel: 280, // diálogos, paneles, acordeones
  exit: 160, // toda salida
  // Con prefers-reduced-motion. En la práctica index.css lleva toda
  // transición CSS a 0.01ms (de golpe), así que este valor solo cuenta para
  // los timeouts de MUI: que el desmontaje no espere de más.
  reduced: 80,
};

// `spring` es un cubic-bezier con un sobrepaso leve y no `linear()`: los PCs de
// hospital suelen correr Chrome viejo, y una función de easing que el
// navegador no entiende invalida la declaración `animation` completa.
export const EASE = {
  out: "cubic-bezier(0.22, 1, 0.36, 1)",
  spring: "cubic-bezier(0.34, 1.3, 0.64, 1)",
};

export const REDUCED_MOTION_QUERY = "@media (prefers-reduced-motion: reduce)";

// Se evalúa al crear el theme (que se recrea al cambiar dark/light). Si el
// médico cambia la preferencia del sistema con la app abierta, aplica al
// recargar; las animaciones CSS de abajo sí reaccionan al instante por la
// media query.
export const prefersReducedMotion = () => {
  try {
    return !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
};

const dialogIn = keyframes`
  from { opacity: 0; transform: scale(0.94) translateY(8px); }
  to   { opacity: 1; transform: none; }
`;

const motionComponents = () => {
  const reduce = prefersReducedMotion();
  const dur = (enter, exit = MOTION.exit) =>
    reduce ? { enter: MOTION.reduced, exit: MOTION.reduced } : { enter, exit };
  return {
    MuiDialog: {
      defaultProps: { transitionDuration: dur(MOTION.panel) },
      styleOverrides: {
        // El Fade del contenedor sigue haciendo la opacidad (y toda la salida);
        // el Paper suma una entrada con escala corta desde el centro.
        paper: {
          animation: `${dialogIn} ${MOTION.panel}ms ${EASE.spring} backwards`,
          [REDUCED_MOTION_QUERY]: { animation: "none" },
        },
        // Pantalla completa (mobile) no escala: se vería el borde de la app.
        paperFullScreen: { animation: "none" },
      },
    },
    // Menu y Popover ya nacen del botón (Grow con transform-origin calculado
    // desde el ancla); acá solo se fijan los tiempos. Menu pasa su propio
    // `transitionDuration` a Popover, por eso van los dos.
    MuiMenu: { defaultProps: { transitionDuration: dur(MOTION.base) } },
    MuiPopover: { defaultProps: { transitionDuration: dur(MOTION.base) } },
    MuiSnackbar: { defaultProps: { transitionDuration: dur(MOTION.base) } },
    MuiCollapse: {
      defaultProps: {
        timeout: reduce ? MOTION.reduced : MOTION.panel,
        easing: { enter: EASE.out, exit: EASE.out },
      },
    },
  };
};

// Fusiona sin pisar: los defaultProps y styleOverrides que ya traiga el theme
// para la misma clave se conservan, y los de movimiento se agregan encima
// solo en las propiedades que el theme no define.
export const withMotion = (theme) => {
  const components = { ...(theme.components || {}) };
  for (const [name, motion] of Object.entries(motionComponents())) {
    const prev = components[name] || {};
    const styleOverrides = { ...(prev.styleOverrides || {}) };
    for (const [slot, style] of Object.entries(motion.styleOverrides || {})) {
      const own = styleOverrides[slot];
      // Un override como función (callback de MUI) no se puede mezclar con un
      // objeto: manda el del theme y el movimiento se omite en ese slot.
      if (typeof own === "function") continue;
      styleOverrides[slot] = { ...style, ...(own || {}) };
    }
    components[name] = {
      ...prev,
      defaultProps: { ...(motion.defaultProps || {}), ...(prev.defaultProps || {}) },
      ...(Object.keys(styleOverrides).length ? { styleOverrides } : {}),
    };
  }
  theme.components = components;
  theme.motion = { ...MOTION, ease: EASE };
  return theme;
};
